import React, { useMemo, useState } from 'react'
import { keyframes } from '@emotion/react'
import { FaRegBell } from 'react-icons/fa6'
import {
    Badge,
    Box,
    Flex,
    Icon,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Text,
    Tooltip,
} from '@chakra-ui/react'

import { LocalStorageKeys } from '~config/constants'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { useAskChckvetDock } from '~components/ask-chckvet/ask-chckvet-context'
import { useClinic } from '~contexts/clinic-context'
import {
    ASK_ROCKET_ALERT_KEY,
    TempAlerts,
} from '~components/temp-alerts/alerts'
import { useDismissedAlerts } from '~components/temp-alerts/use-local-alert-storage'
import { TempAlert } from '~components/temp-alerts/types'

const pulse = keyframes`
    0% {
        transform: scale(1);
        opacity: 0.6;
    }
    100% {
        transform: scale(2.2);
        opacity: 0;
    }
`

/**
 * Header "What's New" bell. Lists product announcements from `TempAlerts`.
 * An alert pulses while it is still before `expiresAt` and this browser has
 * neither opened the popover since it shipped nor dismissed it in the old
 * alerts bell. Opening marks those as read; they keep a "New" tag until the
 * popover closes. Alerts stay in the list after they expire.
 *
 * Read state is per-browser (localStorage), not per-user.
 */
export default function WhatsNewButton() {
    const { clinic } = useClinic()
    const { isEnabled: isAskChckvetEnabled } = useAskChckvetDock()
    const { dismissedKeys } = useDismissedAlerts()
    const [seenIds, setSeenIds] = useLocalStorage<string[]>(
        LocalStorageKeys.whatsNewSeenIds,
        [],
    )
    const [newIdsWhenOpened, setNewIdsWhenOpened] = useState<Set<string>>(
        () => new Set(),
    )

    const alerts = useMemo(
        () =>
            TempAlerts.filter(alert =>
                isAlertVisible(alert, clinic, isAskChckvetEnabled),
            ),
        [clinic, isAskChckvetEnabled],
    )

    const unreadIds = useMemo(() => {
        const seen = new Set<string>(seenIds)
        dismissedKeys.forEach(key => seen.add(key))

        return alerts
            .filter(
                alert =>
                    !seen.has(alert.dismissedKey) && !isAlertExpired(alert),
            )
            .map(alert => alert.dismissedKey)
    }, [alerts, dismissedKeys, seenIds])

    if (alerts.length === 0) return null

    const hasUnread = unreadIds.length > 0

    const handleOpen = () => {
        setNewIdsWhenOpened(new Set(unreadIds))
        if (hasUnread) {
            setSeenIds(prev => Array.from(new Set([...prev, ...unreadIds])))
        }
    }

    return (
        <Popover placement="bottom-start" isLazy onOpen={handleOpen}>
            {({ isOpen, onClose }) => (
                <>
                    <Tooltip
                        label="What's new"
                        openDelay={400}
                        isDisabled={isOpen}
                    >
                        <Box display="inline-flex" flexShrink={0}>
                            <PopoverTrigger>
                                <Box
                                    as="button"
                                    aria-label={
                                        hasUnread
                                            ? `What's new? (${unreadIds.length} unread)`
                                            : "What's new?"
                                    }
                                    p={2}
                                    rounded="full"
                                    position="relative"
                                    display="inline-flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    color={hasUnread ? 'brand.500' : 'gray.500'}
                                    _hover={{ bg: 'brand.50' }}
                                >
                                    {hasUnread && (
                                        <Box
                                            position="absolute"
                                            inset={0}
                                            borderRadius="full"
                                            border="2px solid"
                                            borderColor="brand.400"
                                            animation={`${pulse} 2s ease-out infinite`}
                                            sx={{
                                                '@media (prefers-reduced-motion: reduce)':
                                                    { animation: 'none' },
                                            }}
                                        />
                                    )}
                                    <Icon
                                        as={FaRegBell}
                                        boxSize={4}
                                        position="relative"
                                    />
                                    {hasUnread && (
                                        <Box
                                            position="absolute"
                                            top={1.5}
                                            right={1.5}
                                            w={2}
                                            h={2}
                                            borderRadius="full"
                                            bg="red.500"
                                            boxShadow="0 0 0 2px white"
                                        />
                                    )}
                                </Box>
                            </PopoverTrigger>
                        </Box>
                    </Tooltip>
                    <PopoverContent w="360px" maxW="92vw">
                        <PopoverArrow />
                        <PopoverCloseButton />
                        <PopoverHeader
                            fontWeight="semibold"
                            fontSize="sm"
                            border="none"
                        >
                            What's new?
                        </PopoverHeader>
                        <PopoverBody p={0} maxH="420px" overflowY="auto">
                            {alerts.map((alert, index) => (
                                <WhatsNewItem
                                    key={alert.dismissedKey}
                                    alert={alert}
                                    isNew={newIdsWhenOpened.has(
                                        alert.dismissedKey,
                                    )}
                                    showDivider={index < alerts.length - 1}
                                    onClose={onClose}
                                />
                            ))}
                        </PopoverBody>
                    </PopoverContent>
                </>
            )}
        </Popover>
    )
}

function WhatsNewItem({
    alert,
    isNew,
    showDivider,
    onClose,
}: {
    alert: TempAlert
    isNew: boolean
    showDivider: boolean
    onClose: () => void
}) {
    return (
        <Box
            w="full"
            textAlign="left"
            px={3}
            py={3}
            cursor={alert.action ? 'pointer' : 'default'}
            bg={isNew ? 'brand.50' : 'transparent'}
            _hover={{ bg: isNew ? 'brand.100' : 'gray.50' }}
            borderBottom={showDivider ? '1px solid' : 'none'}
            borderColor="gray.100"
            onClick={event => {
                if (!alert.action) return
                const target = event.target
                if (
                    target instanceof Element &&
                    target.closest('[data-alert-action]')
                ) {
                    return
                }
                activateAlertAction(event.currentTarget)
            }}
        >
            <SharedStack row align="flex-start" spacing={3}>
                {!!alert.icon && (
                    <Flex
                        flexShrink={0}
                        align="center"
                        justify="center"
                        w={8}
                        h={8}
                        rounded="lg"
                        bg="brand.100"
                        color="brand.600"
                    >
                        <Icon as={alert.icon} boxSize={4} />
                    </Flex>
                )}
                <Box flex={1} minW={0}>
                    <Flex align="center" gap={2}>
                        <Text
                            fontSize="sm"
                            fontWeight="semibold"
                            color="gray.800"
                            noOfLines={1}
                        >
                            {alert.title}
                        </Text>
                        {isNew && (
                            <Badge
                                colorScheme="brand"
                                variant="solid"
                                fontSize="2xs"
                                rounded="full"
                                px={1.5}
                            >
                                New
                            </Badge>
                        )}
                    </Flex>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                        {alert.description}
                    </Text>
                    {alert.action && (
                        <Box
                            data-alert-action
                            mt={2}
                            onClick={event => {
                                event.stopPropagation()
                                onClose()
                            }}
                        >
                            {alert.action}
                        </Box>
                    )}
                </Box>
            </SharedStack>
        </Box>
    )
}

/**
 * Ask Rocket eligibility depends on the caller's role, not just the clinic,
 * so it can't be expressed with `otherDisabledChecks`.
 */
function isAlertVisible(
    alert: TempAlert,
    clinic: Parameters<NonNullable<TempAlert['otherDisabledChecks']>>[0],
    isAskChckvetEnabled: boolean,
): boolean {
    if (alert.dismissedKey === ASK_ROCKET_ALERT_KEY && !isAskChckvetEnabled) {
        return false
    }

    return alert.otherDisabledChecks ? alert.otherDisabledChecks(clinic) : true
}

function isAlertExpired(alert: TempAlert): boolean {
    return Date.now() >= new Date(alert.expiresAt).getTime()
}

/** Runs the alert's call-to-action, which is an opaque React node. */
function activateAlertAction(container: HTMLElement) {
    const clickable = container.querySelector<HTMLElement>(
        '[data-alert-action] a, [data-alert-action] button',
    )
    clickable?.click()
}
